/**
 * File Sync Manager
 *
 * Handles syncing offline-stored files to the server when back online.
 * Works with the fileStorage module to upload pending files.
 *
 * After successful file upload to Azure:
 * - For outlet photos: sends uploadOutletPhoto or uploadTpOutletPhoto mutation
 * - For area object photos: the uploaded URL is stored and the entity's patch mutation
 *   should be triggered separately (or the component should listen for FILE_UPLOADED events)
 */

import { v4 as uuidv4 } from 'uuid';
import { BlobServiceClient } from '@azure/storage-blob';
import axios from 'axios';
import { QueryClient } from 'react-query';
import { apiUri } from 'config';
import { authenticationState } from 'app/authentication/state';

import {
  getPendingFileUploads,
  getFileData,
  updateFileUploadStatus,
  cleanupCompletedUploads,
  PendingFileUpload,
  removeFileUpload,
} from './fileStorage';
import { isCurrentlyOnline } from './useOnlineStatus';
import { getFileNameWithFixedJpe } from '../utils';
import { requestGQL } from '../graphQlClient';
import { addLocalToRemoteUrlMapping } from './uploadedPhotoRegistry';
import { persistQueryClient } from './reactQueryPersistence';

/**
 * GraphQL mutations for photo uploads
 */
const UPLOAD_OUTLET_PHOTO = `
  mutation uploadOutletPhoto($propertyId: Int!, $photoUrl: String!, $outletId: String!) {
    uploadOutletPhoto(input: { propertyId: $propertyId, photoUrl: $photoUrl, outletId: $outletId })
  }
`;

const UPLOAD_TPOUTLET_PHOTO = `
  mutation uploadTpOutletPhoto($propertyId: Int!, $photoUrl: String!, $tpOutletId: String!) {
    uploadTpOutletPhoto(input: { propertyId: $propertyId, photoUrl: $photoUrl, tpOutletId: $tpOutletId })
  }
`;

type FileSyncEvent =
  | { type: 'FILE_SYNC_STARTED'; payload: { total: number } }
  | {
      type: 'FILE_UPLOADED';
      payload: { uploadId: string; url: string; entityType: string; entityId: string; propertyId: number };
    }
  | { type: 'FILE_UPLOAD_ERROR'; payload: { uploadId: string; error: string } }
  | { type: 'FILE_SYNC_COMPLETED'; payload: { successful: number; failed: number } };

type FileSyncEventCallback = (event: FileSyncEvent) => void;

/**
 * Get Azure signed link based on upload type
 */
async function getAzureSignedLink(
  uploadType: PendingFileUpload['uploadType'],
): Promise<{ url: string; query: string; container: string }> {
  const { token } = authenticationState();

  if (!token) {
    throw new Error('No authentication token available');
  }

  // TP outlets use same endpoint as regular outlets
  const endpoint =
    uploadType === 'outlet' || uploadType === 'tpOutlet'
      ? `${apiUri}/api/v1/azurestorage/upload/outlet`
      : `${apiUri}/api/v1/azurestorage/upload/objects`;

  const response = await axios.post(endpoint, null, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });

  return response.data;
}

/**
 * Upload a file to Azure Blob Storage
 */
async function uploadToAzure(file: File, signedUrl: string, container: string): Promise<void> {
  const blobServiceClient = new BlobServiceClient(signedUrl);
  const containerClient = blobServiceClient.getContainerClient(container);
  await containerClient.uploadBlockBlob(file.name, file, file.size);
}

/**
 * File Sync Manager class
 */
class FileSyncManager {
  private isSyncing = false;

  private eventListeners: Set<FileSyncEventCallback> = new Set();

  private queryClient: QueryClient | null = null;

  /**
   * Initialize the file sync manager
   * @param queryClient - Optional QueryClient for cache invalidation after uploads
   *
   * NOTE: File sync is now orchestrated by syncManager which calls startSync()
   * with appropriate filters at the right time in the sync sequence:
   * 1. 'object' photos first (for area CREATE mutations)
   * 2. mutations
   * 3. 'outlet'/'tpOutlet' photos last (after outlets exist)
   */
  initialize(queryClient?: QueryClient): void {
    if (queryClient) {
      this.queryClient = queryClient;
    }

    // NOTE: We no longer auto-sync on online status changes here.
    // The syncManager handles the full sync sequence and calls our startSync()
    // with appropriate filters at the right time.
    // This prevents outlet photos from being synced before outlets are created.

    // Clean up old completed uploads on startup
    cleanupCompletedUploads();
  }

  /**
   * Clean up subscriptions
   */
  destroy(): void {
    this.eventListeners.clear();
  }

  /**
   * Subscribe to file sync events
   */
  subscribe(callback: FileSyncEventCallback): () => void {
    this.eventListeners.add(callback);
    return () => {
      this.eventListeners.delete(callback);
    };
  }

  /**
   * Emit a sync event to all listeners
   */
  private emit(event: FileSyncEvent): void {
    this.eventListeners.forEach((callback) => {
      try {
        callback(event);
      } catch {
        // Ignore errors in event listeners
      }
    });
  }

  /**
   * Check if currently syncing
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Start the file sync process
   *
   * @param uploadTypesFilter - Optional array of upload types to sync. If not provided, syncs all.
   *   Use ['object'] to sync only area photos (before mutations).
   *   Use ['outlet', 'tpOutlet'] to sync only outlet photos (after mutations).
   */
  async startSync(uploadTypesFilter?: Array<'object' | 'outlet' | 'tpOutlet'>): Promise<void> {
    if (this.isSyncing) {
      return;
    }

    if (!isCurrentlyOnline()) {
      return;
    }

    let pendingUploads = await getPendingFileUploads();

    // Filter by upload type if specified
    if (uploadTypesFilter && uploadTypesFilter.length > 0) {
      pendingUploads = pendingUploads.filter((u) => uploadTypesFilter.includes(u.uploadType));
    }

    if (pendingUploads.length === 0) {
      return;
    }

    this.isSyncing = true;

    let successful = 0;
    let failed = 0;
    const propertyIdsToRefetch = new Set<number>();

    this.emit({ type: 'FILE_SYNC_STARTED', payload: { total: pendingUploads.length } });

    // Process uploads sequentially to avoid overwhelming the network
    // eslint-disable-next-line no-restricted-syntax
    for (const upload of pendingUploads) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const uploadedUrl = await this.processUpload(upload);

        successful += 1;
        propertyIdsToRefetch.add(upload.propertyId);
        this.emit({
          type: 'FILE_UPLOADED',
          payload: {
            uploadId: upload.id,
            url: uploadedUrl,
            entityType: upload.entityType,
            entityId: upload.entityId,
            propertyId: upload.propertyId,
          },
        });
      } catch (error) {
        failed += 1;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        // eslint-disable-next-line no-await-in-loop
        await updateFileUploadStatus(upload.id, 'failed', undefined, errorMessage);
        this.emit({ type: 'FILE_UPLOAD_ERROR', payload: { uploadId: upload.id, error: errorMessage } });
      }
    }

    this.isSyncing = false;
    this.emit({ type: 'FILE_SYNC_COMPLETED', payload: { successful, failed } });

    // Invalidate queries for properties that had photos uploaded
    // This ensures the UI refreshes with the new real URLs
    if (this.queryClient && propertyIdsToRefetch.size > 0) {
      const propertyIds = Array.from(propertyIdsToRefetch);
      // eslint-disable-next-line no-restricted-syntax
      for (const propertyId of propertyIds) {
        // Refetch outlet-related queries
        // eslint-disable-next-line no-await-in-loop
        await this.queryClient.refetchQueries(['propertyConnection', propertyId]);
        // eslint-disable-next-line no-await-in-loop
        await this.queryClient.refetchQueries(['propertyRadioConnection', propertyId]);
      }

      // Persist the updated query cache to IndexedDB
      // This ensures the new photos survive a page refresh
      await persistQueryClient(this.queryClient);
    }
  }

  /**
   * Process a single file upload
   * Uploads file to Azure and then sends the appropriate GraphQL mutation
   */
  // eslint-disable-next-line class-methods-use-this
  private async processUpload(upload: PendingFileUpload): Promise<string> {
    // Update status to uploading
    await updateFileUploadStatus(upload.id, 'uploading');

    // Get the file data from storage
    const file = await getFileData(upload.id);
    if (!file) {
      throw new Error('File data not found in storage');
    }

    // Get Azure signed link
    const { url, query, container } = await getAzureSignedLink(upload.uploadType);

    // Generate a unique filename
    const fileName = `${uuidv4()}-${getFileNameWithFixedJpe(upload.fileName)}`;
    const renamedFile = new File([file], fileName, { type: file.type });

    // Upload to Azure
    await uploadToAzure(renamedFile, `${url}?${query}`, container);

    // Construct the final URL
    const uploadedUrl = `${url}${container}/${fileName}`;

    // Send the GraphQL mutation to associate the photo with the entity
    await this.sendPhotoMutation(upload, uploadedUrl);

    // Update status to completed
    await updateFileUploadStatus(upload.id, 'completed', uploadedUrl);

    // Remove the file from storage after successful upload and mutation
    await removeFileUpload(upload.id);

    return uploadedUrl;
  }

  /**
   * Send the GraphQL mutation to associate the uploaded photo with its entity
   */
  // eslint-disable-next-line class-methods-use-this
  private async sendPhotoMutation(upload: PendingFileUpload, uploadedUrl: string): Promise<void> {
    const { uploadType, entityId, propertyId, localUrl } = upload;
    // store blob <-> remote url mapping
    if (localUrl) {
      addLocalToRemoteUrlMapping(localUrl, uploadedUrl);
    }
    // For outlet photos, send the mutation directly
    if (uploadType === 'outlet') {
      await requestGQL(UPLOAD_OUTLET_PHOTO, {
        propertyId,
        photoUrl: uploadedUrl,
        outletId: entityId,
      });
    } else if (uploadType === 'tpOutlet') {
      await requestGQL(UPLOAD_TPOUTLET_PHOTO, {
        propertyId,
        photoUrl: uploadedUrl,
        tpOutletId: entityId,
      });
    }
  }

  /**
   * Force upload a specific file
   */
  async forceUpload(uploadId: string): Promise<string | null> {
    if (!isCurrentlyOnline()) {
      return null;
    }

    const uploads = await getPendingFileUploads();
    const upload = uploads.find((u) => u.id === uploadId);

    if (!upload) {
      return null;
    }

    try {
      return await this.processUpload(upload);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await updateFileUploadStatus(upload.id, 'failed', undefined, errorMessage);
      throw error;
    }
  }

  /**
   * Debug method to get status of pending uploads
   * Call from browser console: fileSyncManager.getDebugInfo()
   */
  async getDebugInfo(): Promise<{
    isSyncing: boolean;
    isOnline: boolean;
    hasToken: boolean;
    pendingUploads: PendingFileUpload[];
  }> {
    const { token } = authenticationState();
    const uploads = await getPendingFileUploads();

    const info = {
      isSyncing: this.isSyncing,
      isOnline: isCurrentlyOnline(),
      hasToken: !!token,
      pendingUploads: uploads,
    };

    return info;
  }
}

// Export singleton instance
export const fileSyncManager = new FileSyncManager();

// Export class for testing
export { FileSyncManager };

// Expose to window for debugging in browser console
if (typeof window !== 'undefined') {
  (window as any).fileSyncManager = fileSyncManager;
}
